// Drawing helpers shared across art packs. Pure canvas 2D; no Three.js
// or game state. Each helper is deterministic and side-effect free on
// the ctx it receives.

import { px, rect } from './canvas_utils';

// ---- Color math -----------------------------------------------------

function parse(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = h.length === 3
    ? [h[0] + h[0], h[1] + h[1], h[2] + h[2]].map((s) => parseInt(s, 16))
    : [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return [n[0] ?? 0, n[1] ?? 0, n[2] ?? 0];
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return '#' + [clamp(r), clamp(g), clamp(b)].map((n) => n.toString(16).padStart(2, '0')).join('');
}

export function darken(hex: string, amt: number): string {
  const [r, g, b] = parse(hex);
  return toHex(r * (1 - amt), g * (1 - amt), b * (1 - amt));
}

export function lighten(hex: string, amt: number): string {
  const [r, g, b] = parse(hex);
  return toHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}

export function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  return toHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

// Returns [highlight, light, mid, shade, deep] — 5 shades derived from base.
export function shade5(base: string): [string, string, string, string, string] {
  return [
    lighten(base, 0.4),
    lighten(base, 0.18),
    base,
    darken(base, 0.2),
    darken(base, 0.4),
  ];
}

// ---- Dithering ------------------------------------------------------

// 2x2 Bayer dither. Uses (x+y) parity to pick color — creates a tight
// checker that reads as a blended color at normal camera distance.
export function dither2(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  colorA: string, colorB: string,
) {
  for (let yy = 0; yy < h; yy++) {
    for (let xx = 0; xx < w; xx++) {
      px(ctx, x + xx, y + yy, (xx + yy) % 2 === 0 ? colorA : colorB);
    }
  }
}

// 4x4 ordered dither — percent controls A-vs-B ratio. Good for
// gradual painterly transitions between two shades.
const BAYER4 = [
  0,  8,  2, 10,
  12, 4, 14,  6,
  3, 11,  1,  9,
  15, 7, 13,  5,
];

export function ditherRatio(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  colorA: string, colorB: string, ratio: number,  // 0..1 — fraction of B
) {
  const threshold = Math.floor(ratio * 16);
  for (let yy = 0; yy < h; yy++) {
    for (let xx = 0; xx < w; xx++) {
      const ix = (xx & 3) + (yy & 3) * 4;
      px(ctx, x + xx, y + yy, BAYER4[ix] < threshold ? colorB : colorA);
    }
  }
}

// Vertical gradient painted as discrete steps of `steps` count. Cheap
// replacement for canvas linearGradient (which antialiases into
// sub-pixel gray bands that ruin the pixel-art look).
export function gradientV(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  top: string, bottom: string, steps = 6,
) {
  const bandH = Math.ceil(h / steps);
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    rect(ctx, x, y + i * bandH, w, Math.min(bandH, h - i * bandH), mix(top, bottom, t));
  }
}

// ---- Outlines -------------------------------------------------------

export function outline(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  color = '#000',
) {
  rect(ctx, x, y, w, 1, color);
  rect(ctx, x, y + h - 1, w, 1, color);
  rect(ctx, x, y, 1, h, color);
  rect(ctx, x + w - 1, y, 1, h, color);
}

// ---- Pseudo-random but deterministic --------------------------------

export function hash2(x: number, y: number, seed = 0): number {
  let n = (x * 374761393 + y * 668265263 + seed * 2147483647) | 0;
  n = (n ^ (n >>> 13)) * 1274126177;
  return (n ^ (n >>> 16)) >>> 0;
}
